import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary hover:bg-primary-hover shadow-lg text-font-reverse",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        green: "border-transparent bg-green-500 text-font-reverse",
        outline: "text-foreground",
        danger: "border-transparent bg-red-500 text-font-reverse hover:bg-red-500",
        white: "border-transparent bg-white text-font-primary hover:bg-white",
        orange: "border-transparent bg-orange-500 text-font-reverse hover:bg-orange-500",
        ['default-outline']: "border border-primary/60 text-primary bg-primary/10",
        ['danger-outline']: "border-[0.5px] border-red-500 text-red-500 hover:bg-red-500/10",
        ['orange-outline']: "border-[0.5px] border-orange-500 text-orange-500 hover:bg-orange-500/10",
        ['green-outline']: "border-[0.5px] border-green-500 text-green-500",
        ['green-gradient']: "border-transparent bg-gradient-to-r from-green-500 to-green-600 text-font-reverse",
        ['orange-gradient']: "border-transparent bg-gradient-to-r from-orange-500 to-orange-600 text-font-reverse",
        ['danger-gradient']: "border-transparent bg-gradient-to-r from-red-500 to-red-600 text-font-reverse",
        ['danger-light']: "bg-red-500/20 text-red-500 border-0 hover:bg-red-500/20",
        ['green-light']: "bg-green-500/20 text-green-500 border-0 hover:bg-green-500/20",
      },
      size: {
        default: 'px-1 py-0 text-[9px] md:text-xs md:px-2.5 md:py-0.5',
        xs: 'px-1 py-0 gap-0 item-center text-xs',
        xxs: 'px-1 py-0 gap-0 item-center text-[10px] leading-4',
      },
    },
    defaultVariants: {
      variant: "default",
      size: 'default',
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
